import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, Button, Modal, Form, Input, Select, DatePicker, InputNumber,
  Tag, Space, message, Card, Progress, Popconfirm, Upload, Typography,
  Tooltip, Row, Col, Statistic, Drawer, Descriptions, Divider,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ExportOutlined, ImportOutlined,
  RobotOutlined, SendOutlined, FileExcelOutlined, FilePdfOutlined, ThunderboltOutlined,
  SearchOutlined, EyeOutlined, ExperimentOutlined, WarningOutlined,
  CheckCircleOutlined, ClockCircleOutlined, PauseCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { KSGService } from '../api/services/ksg.service';
import { UsersService } from '../api/services/users.service';
import { ProjectsService } from '../api/services/projects.service';
import SmartMentionsInput from '../components/SmartMentionsInput';

const { TextArea } = Input;
const { Text, Title } = Typography;

const statusOptions = [
  { value: 'active', label: 'Активный', color: 'green' },
  { value: 'completed', label: 'Завершён', color: 'blue' },
  { value: 'archived', label: 'Архив', color: 'default' },
];

const workStatusOptions = [
  { value: 'planned', label: 'Запланирована', color: 'default', icon: <ClockCircleOutlined /> },
  { value: 'in_progress', label: 'В работе', color: 'processing', icon: <SyncOutlined spin /> },
  { value: 'completed', label: 'Завершена', color: 'success', icon: <CheckCircleOutlined /> },
  { value: 'paused', label: 'Приостановлена', color: 'warning', icon: <PauseCircleOutlined /> },
];

const objectTypeOptions = [
  { value: 'residential', label: 'Жилой дом' },
  { value: 'office', label: 'Офис' },
  { value: 'warehouse', label: 'Склад' },
  { value: 'shopping', label: 'Торговый центр' },
  { value: 'production', label: 'Производство' },
  { value: 'social', label: 'Социальный объект' },
  { value: 'sports', label: 'Спортивный объект' },
  { value: 'infrastructure', label: 'Инфраструктура' },
  { value: 'other', label: 'Другое' },
];

export default function KSGPage() {
  const navigate = useNavigate();
  const [objects, setObjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState(null);

  const [objectModalOpen, setObjectModalOpen] = useState(false);
  const [editingObject, setEditingObject] = useState(null);
  const [objectForm] = Form.useForm();

  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [selectedObject, setSelectedObject] = useState(null);
  const [objectDetail, setObjectDetail] = useState(null);

  const [workModalOpen, setWorkModalOpen] = useState(false);
  const [editingWork, setEditingWork] = useState(null);
  const [workForm] = Form.useForm();
  const [workObjectId, setWorkObjectId] = useState(null);

  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportForm] = Form.useForm();

  const [aiChatOpen, setAiChatOpen] = useState(false);
  const [aiSessionId, setAiSessionId] = useState(null);
  const [aiMessages, setAiMessages] = useState([]);
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiDraft, setAiDraft] = useState(null);
  const chatEndRef = useRef(null);

  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [aiPanelResult, setAiPanelResult] = useState(null);
  const [aiPanelType, setAiPanelType] = useState('');
  const [aiPanelLoading, setAiPanelLoading] = useState(false);

  const [gpzuModalOpen, setGpzuModalOpen] = useState(false);
  const [gpzuData, setGpzuData] = useState(null);
  const [gpzuLoading, setGpzuLoading] = useState(false);
  const [gpzuFeasibilityOpen, setGpzuFeasibilityOpen] = useState(false);
  const [gpzuFeasibilityResult, setGpzuFeasibilityResult] = useState(null);
  const [gpzuFeasibilityLoading, setGpzuFeasibilityLoading] = useState(false);
  const [gpzuFeasibilityForm] = Form.useForm();

  const fetchObjects = () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (filterStatus) params.status = filterStatus;
    KSGService.getObjects(params)
      .then(r => setObjects(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const fetchUsers = () => UsersService.getUsers().then(r => setUsers(r.data)).catch(() => {});
  const fetchProjects = () => ProjectsService.getProjects().then(r => setProjects(r.data)).catch(() => {});
  const fetchTemplates = () => KSGService.getTemplatesList().then(r => setTemplates(r.data)).catch(() => {});

  useEffect(() => { fetchObjects(); fetchUsers(); fetchProjects(); fetchTemplates(); }, []);
  useEffect(() => { fetchObjects(); }, [search, filterStatus]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [aiMessages]);

  const handleCreateObject = async (values) => {
    const payload = {
      ...values,
      assignee_id: values.assignee_id || null,
      project_id: values.project_id || null,
    };
    
    if (editingObject) {
      await KSGService.updateObject(editingObject.id, payload);
      message.success('Объект обновлён');
    } else if (values.template_id) {
      // Создаём объект из шаблона
      const { template_id, ...objectData } = payload;
      await KSGService.createObjectFromTemplate(template_id, objectData);
      message.success('Объект КСГ создан из шаблона');
    } else {
      await KSGService.createObject(payload);
      message.success('Объект КСГ создан');
    }
    setObjectModalOpen(false);
    setEditingObject(null);
    objectForm.resetFields();
    fetchObjects();
  };

  const handleDeleteObject = async (id) => {
    await KSGService.deleteObject(id);
    message.success('Объект удалён');
    fetchObjects();
  };

  const handleOpenDetail = async (record) => {
    setSelectedObject(record);
    setDetailDrawerOpen(true);
    try {
      const res = await KSGService.getObject(record.id);
      setObjectDetail(res.data);
    } catch {
      setObjectDetail(null);
    }
  };

  const handleCreateWork = async (values) => {
    const payload = {
      ...values,
      object_id: editingWork ? editingWork.object_id : workObjectId,
      plan_start: values.plan_start?.format('YYYY-MM-DD') || null,
      plan_end: values.plan_end?.format('YYYY-MM-DD') || null,
      fact_start: values.fact_start?.format('YYYY-MM-DD') || null,
      fact_end: values.fact_end?.format('YYYY-MM-DD') || null,
    };
    if (editingWork) {
      await KSGService.updateWork(editingWork.id, payload);
      message.success('Работа обновлена');
    } else {
      await KSGService.createWork(payload);
      message.success('Работа создана');
    }
    setWorkModalOpen(false);
    setEditingWork(null);
    workForm.resetFields();
    if (selectedObject) handleOpenDetail(selectedObject);
    fetchObjects();
  };

  const handleDeleteWork = async (id) => {
    await KSGService.deleteWork(id);
    message.success('Работа удалена');
    if (selectedObject) handleOpenDetail(selectedObject);
    fetchObjects();
  };

  const handleExportExcel = async (objectId) => {
    try {
      const res = await KSGService.exportWorksExcel(objectId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `KSG_${objectId}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      message.success('Экспорт завершён');
    } catch {
      message.error('Ошибка экспорта');
    }
  };

  const handleImportExcel = async (objectId, file) => {
    try {
      await KSGService.importWorksExcel(objectId, file);
      message.success('Импорт завершён');
      if (selectedObject?.id === objectId) handleOpenDetail(selectedObject);
      fetchObjects();
    } catch {
      message.error('Ошибка импорта');
    }
    return false;
  };

  const handleExportToTasks = async (values) => {
    if (!selectedObject) return;
    try {
      const res = await KSGService.exportToTasks(selectedObject.id, values);
      message.success(`Создан проект "${res.data.project_name}" с ${res.data.tasks_created} задачами`);
      setExportModalOpen(false);
      exportForm.resetFields();
    } catch {
      message.error('Ошибка экспорта в задачи');
    }
  };

  const startAiChat = async () => {
    setAiChatOpen(true);
    setAiMessages([]);
    setAiDraft(null);
    setAiLoading(true);
    try {
      const res = await KSGService.aiChatStart({});
      setAiSessionId(res.data.session_id);
      setAiMessages([{ role: 'assistant', content: res.data.ai_message }]);
    } catch {
      message.error('Ошибка запуска ИИ. Проверьте что Ollama установлена.');
      setAiChatOpen(false);
    } finally {
      setAiLoading(false);
    }
  };

  const handleGpzuUpload = async (file) => {
    setGpzuLoading(true);
    setGpzuData(null);
    try {
      const res = await KSGService.aiGpzuAnalyze(file);
      setGpzuData(res.data);
      message.success('ГПЗУ проанализирован');
    } catch {
      message.error('Ошибка анализа ГПЗУ');
    } finally {
      setGpzuLoading(false);
    }
    return false;
  };

  const handleGpzuFeasibility = async (values) => {
    if (!gpzuData) return;
    setGpzuFeasibilityLoading(true);
    setGpzuFeasibilityResult(null);
    try {
      const res = await KSGService.aiGpzuFeasibility(gpzuData, values);
      setGpzuFeasibilityResult(res.data.analysis);
      setGpzuFeasibilityOpen(true);
    } catch {
      message.error('Ошибка анализа возможности строительства');
    } finally {
      setGpzuFeasibilityLoading(false);
    }
  };

  const sendAiMessage = async () => {
    if (!aiInput.trim() || !aiSessionId || aiLoading) return;
    const userMsg = aiInput.trim();
    setAiInput('');
    setAiMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setAiLoading(true);
    try {
      const res = await KSGService.aiChatMessage({ session_id: aiSessionId, message: userMsg });
      const aiMsg = res.data.ai_message || '';
      setAiMessages(prev => [...prev, { role: 'assistant', content: aiMsg }]);
      if (res.data.is_complete && res.data.draft) {
        setAiDraft(res.data.draft);
      }
    } catch (err) {
      const errorMsg = err?.response?.data?.detail || err?.message || 'Ошибка соединения с сервером';
      setAiMessages(prev => [...prev, { role: 'assistant', content: `Ошибка: ${errorMsg}` }]);
    } finally {
      setAiLoading(false);
    }
  };

  const createFromDraft = async () => {
    if (!aiDraft) return;
    try {
      const objData = aiDraft.object;
      const res = await KSGService.createObject({
        name: objData.name || 'Объект из ИИ',
        code: objData.code || `KSG-AI-${Date.now()}`,
        description: objData.description || '',
        address: objData.address || null,
        object_type: objData.object_type || null,
      });
      const objectId = res.data.id;
      if (aiDraft.works?.length) {
        const works = aiDraft.works.map((w, i) => ({
          name: w.name,
          unit: w.unit || null,
          volume: w.volume || null,
          plan_start: w.plan_start || null,
          plan_end: w.plan_end || null,
          notes: w.notes || null,
          sort_order: i,
        }));
        await KSGService.createWorksBulk(objectId, works);
      }
      message.success('Объект создан из ИИ-эскиза');
      setAiChatOpen(false);
      fetchObjects();
    } catch {
      message.error('Ошибка создания объекта');
    }
  };

  const runAiAnalysis = async (type) => {
    if (!selectedObject) return;
    setAiPanelType(type);
    setAiPanelOpen(true);
    setAiPanelLoading(true);
    setAiPanelResult(null);
    const fn = { estimate: KSGService.aiEstimate, risks: KSGService.aiRisks, optimize: KSGService.aiOptimize, suggest: KSGService.aiSuggest }[type];
    try {
      const res = await fn(selectedObject.id);
      setAiPanelResult(res.data.analysis);
    } catch {
      setAiPanelResult('Ошибка анализа. Проверьте что Ollama установлена.');
    } finally {
      setAiPanelLoading(false);
    }
  };

  const userName = (id) => {
    const u = users.find(u => String(u.id) === String(id));
    return u ? (u.full_name || u.email) : null;
  };

  const statusTag = (status) => {
    const opt = statusOptions.find(o => o.value === status);
    return opt ? <Tag color={opt.color}>{opt.label}</Tag> : <Tag>{status}</Tag>;
  };

  const workStatusTag = (status) => {
    const opt = workStatusOptions.find(o => o.value === status);
    return opt ? <Tag color={opt.color} icon={opt.icon}>{opt.label}</Tag> : <Tag>{status}</Tag>;
  };

  const objectColumns = [
    {
      title: 'Код', dataIndex: 'code', key: 'code', width: 120,
      sorter: (a, b) => (a.code || '').localeCompare(b.code || ''),
    },
    {
      title: 'Название', dataIndex: 'name', key: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
      render: (text, record) => (
        <Button type="link" onClick={() => handleOpenDetail(record)} style={{ padding: 0, fontWeight: 600 }}>
          {text}
        </Button>
      ),
    },
    { title: 'Тип', dataIndex: 'object_type', key: 'object_type', width: 140,
      render: (t) => objectTypeOptions.find(o => o.value === t)?.label || t || '—',
    },
    { title: 'Адрес', dataIndex: 'address', key: 'address', width: 200, ellipsis: true,
      render: (v) => v || '—',
    },
    { title: 'Статус', dataIndex: 'status', key: 'status', width: 120,
      filters: statusOptions.map(o => ({ text: o.label, value: o.value })),
      onFilter: (v, r) => r.status === v,
      render: (s) => statusTag(s),
    },
    { title: 'Ответственный', dataIndex: 'assignee_id', key: 'assignee_id', width: 180,
      render: (id) => userName(id) || '—',
    },
    {
      title: 'Действия', key: 'actions', width: 180,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Просмотр">
            <Button size="small" icon={<EyeOutlined />} onClick={() => handleOpenDetail(record)} />
          </Tooltip>
          <Tooltip title="Редактировать">
            <Button size="small" icon={<EditOutlined />} onClick={() => {
              setEditingObject(record);
              objectForm.setFieldsValue({
                ...record,
                plan_start: record.plan_start ? dayjs(record.plan_start) : null,
              });
              setObjectModalOpen(true);
            }} />
          </Tooltip>
          <Popconfirm title="Удалить объект со всеми работами?" onConfirm={() => handleDeleteObject(record.id)} okText="Удалить" cancelText="Отмена">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const workColumns = [
    { title: '№', key: 'sort_order', width: 50, render: (_, __, i) => i + 1 },
    { title: 'Название работы', dataIndex: 'name', key: 'name',
      render: (text) => <Text strong>{text}</Text>,
    },
    { title: 'Ед.', dataIndex: 'unit', key: 'unit', width: 80 },
    { title: 'Объём', dataIndex: 'volume', key: 'volume', width: 100,
      render: (v) => v != null ? v.toLocaleString() : '—',
    },
    { title: 'Статус', dataIndex: 'status', key: 'status', width: 140,
      render: (s) => workStatusTag(s),
    },
    { title: 'Прогресс', dataIndex: 'progress', key: 'progress', width: 140,
      render: (v) => <Progress percent={v || 0} size="small" status={(v || 0) >= 100 ? 'success' : 'active'} />,
    },
    { title: 'План', key: 'plan', width: 180,
      render: (_, r) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {r.plan_start ? dayjs(r.plan_start).format('DD.MM') : '—'} — {r.plan_end ? dayjs(r.plan_end).format('DD.MM') : '—'}
        </Text>
      ),
    },
    {
      title: 'Действия', key: 'actions', width: 100,
      render: (_, record) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => {
            setEditingWork(record);
            workForm.setFieldsValue({
              ...record,
              plan_start: record.plan_start ? dayjs(record.plan_start) : null,
              plan_end: record.plan_end ? dayjs(record.plan_end) : null,
              fact_start: record.fact_start ? dayjs(record.fact_start) : null,
              fact_end: record.fact_end ? dayjs(record.fact_end) : null,
            });
            setWorkModalOpen(true);
          }} />
          <Popconfirm title="Удалить работу?" onConfirm={() => handleDeleteWork(record.id)} okText="Удалить" cancelText="Отмена">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const expandedRowRender = (record) => {
    const [works, setWorks] = useState([]);
    const [worksLoading, setWorksLoading] = useState(false);

    useEffect(() => {
      setWorksLoading(true);
      KSGService.getWorks(record.id)
        .then(r => setWorks(r.data))
        .catch(() => {})
        .finally(() => setWorksLoading(false));
    }, [record.id]);

    return (
      <Table
        dataSource={works}
        columns={workColumns}
        rowKey="id"
        size="small"
        loading={worksLoading}
        pagination={false}
      />
    );
  };

  return (
    <div className="page-container">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="page-title">КСГ</h1>
          <p className="page-subtitle">Комплексные стандартные группы</p>
        </div>
        <Space>
          <Button
            icon={<ThunderboltOutlined />}
            onClick={() => navigate('/ksg/wizard')}
            style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', border: 'none', color: '#fff', fontWeight: 600 }}
          >
            Создать КСГ
          </Button>
          <Button icon={<FilePdfOutlined />} onClick={() => { setGpzuData(null); setGpzuModalOpen(true); }}
            style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', border: 'none', color: '#fff' }}>
            Анализ ГПЗУ
          </Button>
          <Button icon={<RobotOutlined />} onClick={startAiChat}
            style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', border: 'none', color: '#fff' }}>
            Создать с ИИ
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => {
            setEditingObject(null);
            objectForm.resetFields();
            setObjectModalOpen(true);
          }} style={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', border: 'none' }}>
            Новый объект
          </Button>
        </Space>
      </div>

      <Card style={{ marginBottom: 20, borderRadius: 16 }} styles={{ body: { padding: '16px 20px' } }}>
        <Space style={{ flexWrap: 'wrap', width: '100%' }} size={[12, 12]}>
          <Input
            placeholder="Поиск по названию или коду..."
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 280 }}
            allowClear
          />
          <Select
            allowClear
            placeholder="Статус"
            value={filterStatus}
            onChange={setFilterStatus}
            options={statusOptions.map(o => ({ value: o.value, label: o.label }))}
            style={{ width: 160 }}
          />
        </Space>
      </Card>

      <Card style={{ borderRadius: 16 }} styles={{ body: { padding: 0 } }}>
        <Table
          dataSource={objects}
          columns={objectColumns}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
          expandable={{
            expandedRowRender,
            rowExpandable: () => true,
          }}
        />
      </Card>

      <Modal
        title={editingObject ? 'Редактировать объект' : 'Новый объект КСГ'}
        open={objectModalOpen}
        onCancel={() => { setObjectModalOpen(false); setEditingObject(null); objectForm.resetFields(); }}
        onOk={() => objectForm.submit()}
        okText={editingObject ? 'Сохранить' : 'Создать'}
        width={640}
      >
        <Form form={objectForm} layout="vertical" onFinish={handleCreateObject}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="code" label="Код" rules={[{ required: true, message: 'Введите код' }]}>
                <Input placeholder="КСГ-2026-001" />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item name="name" label="Название" rules={[{ required: true, message: 'Введите название' }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
          {!editingObject && (
            <Form.Item name="template_id" label="Шаблон (опционально)">
              <Select 
                allowClear 
                placeholder="Выберите шаблон для автоматического заполнения работ"
                options={templates.map(t => ({ 
                  value: t.id, 
                  label: (
                    <Space>
                      <span>{t.name}</span>
                      <Tag color="blue">{t.tasks_count} задач</Tag>
                    </Space>
                  )
                }))}
                optionFilterProp="label"
                showSearch
              />
            </Form.Item>
          )}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="object_type" label="Тип объекта">
                <Select allowClear options={objectTypeOptions} placeholder="Выберите тип" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="Статус" initialValue="active">
                <Select options={statusOptions} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="address" label="Адрес">
            <Input placeholder="г. Екатеринбург, ..." />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="assignee_id" label="Ответственный">
                <Select allowClear showSearch placeholder="Выберите" options={users.map(u => ({ value: u.id, label: u.full_name || u.email }))}
                  filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="contractor" label="Подрядчик">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="Описание">
            <SmartMentionsInput rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={selectedObject ? `${selectedObject.name} (${selectedObject.code})` : 'Детали объекта'}
        open={detailDrawerOpen}
        onClose={() => { setDetailDrawerOpen(false); setSelectedObject(null); setObjectDetail(null); }}
        width={900}
        extra={
          <Space>
            <Button icon={<FileExcelOutlined />} onClick={() => handleExportExcel(selectedObject?.id)}>Excel</Button>
            <Upload beforeUpload={(file) => { handleImportExcel(selectedObject?.id, file); return false; }} showUploadList={false}>
              <Button icon={<ImportOutlined />}>Импорт</Button>
            </Upload>
            <Button icon={<ThunderboltOutlined />} onClick={() => {
              exportForm.resetFields();
              exportForm.setFieldsValue({ project_name: selectedObject?.name, task_type: 'design_engineering' });
              setExportModalOpen(true);
            }}>В задачи</Button>
            <Button icon={<ExperimentOutlined />} onClick={() => runAiAnalysis('estimate')}>ИИ: Сроки</Button>
            <Button icon={<WarningOutlined />} onClick={() => runAiAnalysis('risks')}>ИИ: Риски</Button>
          </Space>
        }
      >
        {objectDetail ? (
          <>
            <Descriptions column={2} size="small" bordered style={{ marginBottom: 20 }}>
              <Descriptions.Item label="Тип">{objectTypeOptions.find(o => o.value === objectDetail.object_type)?.label || objectDetail.object_type || '—'}</Descriptions.Item>
              <Descriptions.Item label="Статус">{statusTag(objectDetail.status)}</Descriptions.Item>
              <Descriptions.Item label="Адрес" span={2}>{objectDetail.address || '—'}</Descriptions.Item>
              <Descriptions.Item label="Ответственный">{userName(objectDetail.assignee_id) || '—'}</Descriptions.Item>
              <Descriptions.Item label="Подрядчик">{objectDetail.contractor || '—'}</Descriptions.Item>
              <Descriptions.Item label="Описание" span={2}>{objectDetail.description || '—'}</Descriptions.Item>
            </Descriptions>

            {objectDetail.stats && (
              <Row gutter={16} style={{ marginBottom: 20 }}>
                <Col span={6}>
                  <Card size="small"><Statistic title="Всего работ" value={objectDetail.stats.total_works} /></Card>
                </Col>
                <Col span={6}>
                  <Card size="small"><Statistic title="Завершено" value={objectDetail.stats.completed_works} valueStyle={{ color: '#52c41a' }} /></Card>
                </Col>
                <Col span={6}>
                  <Card size="small"><Statistic title="Общая стоимость" value={objectDetail.stats.total_cost} formatter={(v) => `${v?.toLocaleString()} ₽`} /></Card>
                </Col>
                <Col span={6}>
                  <Card size="small"><Statistic title="Средний прогресс" value={objectDetail.stats.avg_progress} suffix="%" /></Card>
                </Col>
              </Row>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <Title level={5} style={{ margin: 0 }}>Работы ({objectDetail.works?.length || 0})</Title>
              <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => {
                setEditingWork(null);
                setWorkObjectId(objectDetail.id);
                workForm.resetFields();
                workForm.setFieldsValue({ status: 'planned' });
                setWorkModalOpen(true);
              }}>
                Добавить работу
              </Button>
            </div>
            <Table
              dataSource={objectDetail.works || []}
              columns={workColumns}
              rowKey="id"
              size="small"
              pagination={false}
            />
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: 40 }}>Загрузка...</div>
        )}
      </Drawer>

      <Modal
        title={editingWork ? 'Редактировать работу' : 'Новая работа'}
        open={workModalOpen}
        onCancel={() => { setWorkModalOpen(false); setEditingWork(null); workForm.resetFields(); }}
        onOk={() => workForm.submit()}
        okText={editingWork ? 'Сохранить' : 'Создать'}
        width={640}
      >
        <Form form={workForm} layout="vertical" onFinish={handleCreateWork}>
          <Form.Item name="name" label="Название работы" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="unit" label="Ед. измерения">
                <Input placeholder="м2, м3, шт..." />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="volume" label="Объём">
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="price_per_unit" label="Цена за ед.">
                <InputNumber style={{ width: '100%' }} formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="total_cost" label="Стоимость">
                <InputNumber style={{ width: '100%' }} formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="status" label="Статус" initialValue="planned">
                <Select options={workStatusOptions} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="progress" label="Прогресс %">
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="plan_start" label="План начало"><DatePicker style={{ width: '100%' }} /></Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="plan_end" label="План конец"><DatePicker style={{ width: '100%' }} /></Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="fact_start" label="Факт начало"><DatePicker style={{ width: '100%' }} /></Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="fact_end" label="Факт конец"><DatePicker style={{ width: '100%' }} /></Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="assignee_id" label="Исполнитель">
                <Select allowClear showSearch placeholder="Выберите" options={users.map(u => ({ value: u.id, label: u.full_name || u.email }))}
                  filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="contractor" label="Подрядчик">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="notes" label="Примечания">
            <TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Экспорт КСГ в задачи"
        open={exportModalOpen}
        onCancel={() => setExportModalOpen(false)}
        onOk={() => exportForm.submit()}
        okText="Экспортировать"
      >
        <Form form={exportForm} layout="vertical" onFinish={handleExportToTasks}>
          <Form.Item name="project_name" label="Название проекта" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="assignee_id" label="Исполнитель задач">
            <Select allowClear showSearch placeholder="Выберите" options={users.map(u => ({ value: u.id, label: u.full_name || u.email }))}
              filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())} />
          </Form.Item>
          <Form.Item name="task_type" label="Тип задач" initialValue="design_engineering">
            <Select options={[
              { value: 'land_legal', label: 'Земельно-правовые' },
              { value: 'permissive', label: 'Разрешительные' },
              { value: 'design_engineering', label: 'Проектно-инженерные' },
              { value: 'logistics', label: 'Логистические' },
              { value: 'coordination', label: 'Координационные' },
            ]} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="ИИ-ассистент КСГ"
        open={aiChatOpen}
        onCancel={() => setAiChatOpen(false)}
        footer={null}
        width={700}
        styles={{ body: { padding: 0 } }}
      >
        <div style={{ height: 400, overflowY: 'auto', padding: 16 }}>
          {aiMessages.map((msg, i) => (
            <div key={i} style={{
              display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              marginBottom: 12,
            }}>
              <div style={{
                maxWidth: '80%', padding: '10px 14px', borderRadius: 12,
                background: msg.role === 'user' ? '#6366f1' : '#f1f5f9',
                color: msg.role === 'user' ? '#fff' : '#1e293b',
                whiteSpace: 'pre-wrap',
              }}>
                {msg.content}
              </div>
            </div>
          ))}
          {aiLoading && (
            <div style={{ textAlign: 'center', padding: 8, color: '#94a3b8' }}>
              <SyncOutlined spin /> ИИ думает...
            </div>
          )}
          <div ref={chatEndRef} />
        </div>
        {aiDraft && (
          <div style={{ padding: '12px 16px', background: '#f0fdf4', borderTop: '1px solid #bbf7d0' }}>
            <Text strong style={{ color: '#166534' }}>Эскиз готов!</Text>
            <Text> Объект: {aiDraft.object?.name}, работ: {aiDraft.works?.length || 0}</Text>
            <Button type="primary" size="small" style={{ marginLeft: 12 }} onClick={createFromDraft}>
              Создать объект
            </Button>
          </div>
        )}
        <div style={{ padding: 12, borderTop: '1px solid #e2e8f0', display: 'flex', gap: 8 }}>
          <Input
            placeholder="Ваш ответ..."
            value={aiInput}
            onChange={(e) => setAiInput(e.target.value)}
            onPressEnter={sendAiMessage}
            disabled={aiLoading}
          />
          <Button type="primary" icon={<SendOutlined />} onClick={sendAiMessage} loading={aiLoading} />
        </div>
      </Modal>

      <Drawer
        title={
          aiPanelType === 'estimate' ? 'ИИ: Оценка сроков' :
          aiPanelType === 'risks' ? 'ИИ: Анализ рисков' :
          aiPanelType === 'optimize' ? 'ИИ: Оптимизация плана' :
          'ИИ: Предложения'
        }
        open={aiPanelOpen}
        onClose={() => { setAiPanelOpen(false); setAiPanelResult(null); }}
        width={600}
      >
        {aiPanelLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <SyncOutlined spin style={{ fontSize: 32, color: '#6366f1' }} />
            <div style={{ marginTop: 16, color: '#64748b' }}>Анализирую...</div>
          </div>
        ) : (
          <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{aiPanelResult}</div>
        )}
      </Drawer>

      <Modal
        title="Анализ ГПЗУ"
        open={gpzuModalOpen}
        onCancel={() => setGpzuModalOpen(false)}
        footer={null}
        width={800}
      >
        <div style={{ marginBottom: 16 }}>
          <Text>Загрузите ГПЗУ (градостроительный план земельного участка) в формате PDF для анализа.</Text>
        </div>
        <Upload.Dragger
          accept=".pdf"
          beforeUpload={handleGpzuUpload}
          showUploadList={false}
          disabled={gpzuLoading}
        >
          <p style={{ fontSize: 32, color: '#d97706' }}><FilePdfOutlined /></p>
          <p>Нажмите или перетащите PDF файл сюда</p>
        </Upload.Dragger>
        {gpzuLoading && (
          <div style={{ textAlign: 'center', padding: 40, background: '#fffbeb', borderRadius: 8, marginTop: 16 }}>
            <SyncOutlined spin style={{ fontSize: 48, color: '#d97706' }} />
            <Title level={4} style={{ marginTop: 16, color: '#92400e' }}>Анализирую ГПЗУ...</Title>
            <Text type="secondary">Это может занять до 30 секунд. Пожалуйста, подождите.</Text>
          </div>
        )}
        {gpzuData && !gpzuData.error && (
          <div style={{ marginTop: 20 }}>
            <Title level={5}>Результаты анализа</Title>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Кадастровый номер" span={2}>{gpzuData.cadastral_number || '—'}</Descriptions.Item>
              <Descriptions.Item label="Адрес" span={2}>{gpzuData.address || '—'}</Descriptions.Item>
              <Descriptions.Item label="Площадь участка">{gpzuData.area_sqm ? `${gpzuData.area_sqm} кв.м` : '—'}</Descriptions.Item>
              <Descriptions.Item label="Категория земель">{gpzuData.land_category || '—'}</Descriptions.Item>
              <Descriptions.Item label="ВРИ">{gpzuData.permitted_use || '—'}</Descriptions.Item>
              <Descriptions.Item label="Зона зонирования">{gpzuData.zone || '—'}</Descriptions.Item>
              <Descriptions.Item label="Макс. % застройки">{gpzuData.max_building_percent ? `${gpzuData.max_building_percent}%` : '—'}</Descriptions.Item>
              <Descriptions.Item label="Макс. высота">{gpzuData.max_height_m ? `${gpzuData.max_height_m} м` : '—'}</Descriptions.Item>
              <Descriptions.Item label="Макс. этажей">{gpzuData.max_floors || '—'}</Descriptions.Item>
              <Descriptions.Item label="Мин. отступы">{gpzuData.min_setbacks_m ? `${gpzuData.min_setbacks_m} м` : '—'}</Descriptions.Item>
              <Descriptions.Item label="Строительство разрешено" span={2}>
                {gpzuData.construction_allowed ? <Tag color="success">Да</Tag> : <Tag color="error">Нет / Ограничено</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="Разрешённые типы" span={2}>
                {gpzuData.allowed_types?.length ? gpzuData.allowed_types.map(t => <Tag key={t}>{t}</Tag>) : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Макс. общая площадь">{gpzuData.max_total_area_sqm ? `${gpzuData.max_total_area_sqm} кв.м` : '—'}</Descriptions.Item>
              <Descriptions.Item label="Макс. площадь застройки">{gpzuData.max_footprint_sqm ? `${gpzuData.max_footprint_sqm} кв.м` : '—'}</Descriptions.Item>
              <Descriptions.Item label="Мин. парковочных мест">{gpzuData.parking_spaces_min || '—'}</Descriptions.Item>
              <Descriptions.Item label="Ограничения" span={2}>
                {gpzuData.restrictions?.length ? <ul style={{ margin: 0, paddingLeft: 16 }}>{gpzuData.restrictions.map((r, i) => <li key={i}>{r}</li>)}</ul> : 'Нет'}
              </Descriptions.Item>
              <Descriptions.Item label="Риски" span={2}>
                {gpzuData.risks?.length ? <ul style={{ margin: 0, paddingLeft: 16 }}>{gpzuData.risks.map((r, i) => <li key={i}>{r}</li>)}</ul> : 'Нет'}
              </Descriptions.Item>
              <Descriptions.Item label="Рекомендации" span={2}>{gpzuData.recommendations || '—'}</Descriptions.Item>
            </Descriptions>
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <Button type="primary" icon={<ThunderboltOutlined />} onClick={() => {
                gpzuFeasibilityForm.resetFields();
                setGpzuFeasibilityResult(null);
                setGpzuFeasibilityOpen(true);
              }}>
                Проверить возможность строительства
              </Button>
            </div>
          </div>
        )}
        {gpzuData?.error && (
          <div style={{ marginTop: 16, padding: 16, background: '#fef2f2', borderRadius: 8 }}>
            <Text type="danger">{gpzuData.error}</Text>
            {gpzuData.raw_analysis && <div style={{ marginTop: 8, whiteSpace: 'pre-wrap', fontSize: 12 }}>{gpzuData.raw_analysis}</div>}
          </div>
        )}
      </Modal>

      <Drawer
        title="Возможность строительства"
        open={gpzuFeasibilityOpen}
        onClose={() => { setGpzuFeasibilityOpen(false); setGpzuFeasibilityResult(null); }}
        width={600}
        extra={
          <Form form={gpzuFeasibilityForm} layout="inline" onFinish={handleGpzuFeasibility}>
            <Form.Item name="object_type" initialValue="residential">
              <Select size="small" options={objectTypeOptions} style={{ width: 140 }} />
            </Form.Item>
            <Form.Item name="floors">
              <InputNumber size="small" placeholder="Этажи" min={1} max={50} style={{ width: 80 }} />
            </Form.Item>
            <Form.Item name="total_area_sqm">
              <InputNumber size="small" placeholder="Общая пл. кв.м" min={1} style={{ width: 120 }} />
            </Form.Item>
            <Form.Item>
              <Button type="primary" size="small" htmlType="submit" loading={gpzuFeasibilityLoading} icon={<ThunderboltOutlined />}>
                Анализ
              </Button>
            </Form.Item>
          </Form>
        }
      >
        {gpzuFeasibilityLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <SyncOutlined spin style={{ fontSize: 32, color: '#d97706' }} />
            <div style={{ marginTop: 16, color: '#64748b' }}>Проверяю возможность строительства...</div>
          </div>
        ) : gpzuFeasibilityResult ? (
          <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{gpzuFeasibilityResult}</div>
        ) : (
          <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
            Выберите параметры объекта и нажмите "Анализ"
          </div>
        )}
      </Drawer>
    </div>
  );
}
